import React, { useState, useEffect, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Badge, Button } from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { Tooltip, IconButton } from "@mui/material";
import { MdOutlineAddCard } from "react-icons/md";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import axios from "axios";
import AgGridPagination from "../../shared/AgGridPagination";

ModuleRegistry.registerModules([AllCommunityModule]);

const rowPerPage = 10;

type ReceiverAccountData = {
  id: string;
  created_at: string;
  asset: string;
  method_name: string;
  method_details: Record<string, any>;
  confirm_status: string;
};

interface ReceiverAccountSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (accountData: ReceiverAccountData) => void;
  receiverId: string;
  currency: string;
}

interface PayloadType {
  [key: string]: any;
}

const ReceiverAccountSelectorModal: React.FC<
  ReceiverAccountSelectorModalProps
> = ({ isOpen, onClose, onSelect, receiverId, currency }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tableData, setTableData] = useState<ReceiverAccountData[]>([]);

  // Pagination state following receiver/index.tsx pattern
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(rowPerPage);
  const [rowCount, setRowCount] = useState<number>(rowPerPage);

  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  // Fetch when modal opens
  useEffect(() => {
    if (isOpen && receiverId) {
      setTableData([]);
      setPage(0);
      setLastKeys({});
      setCurrentLastKey("");
      setRowCount(rowPerPage);
      fetchReceiverAccounts({
        page: 0,
        limit: pageSize,
        lastkey: "",
        receiver_id: receiverId,
      });
    }
  }, [isOpen, receiverId]);

  const fetchReceiverAccounts = async (payload: PayloadType = {}) => {
    try {
      setIsLoading(true);

      const queryObject: PayloadType = {
        limit: payload?.limit || pageSize,
        lastkey: payload?.lastkey || "",
        receiver_id: payload?.receiver_id || receiverId,
        usertype: userRole === "superadmin" ? "a" : "m",
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/receiveraccount/get-all?${queryString}`
      );

      const newData: ReceiverAccountData[] =
        response.data.receiver_accounts?.payload || [];

      // Filter by currency/asset
      const filteredData = newData.filter(
        (account) => account.asset === currency
      );

      if (filteredData.length !== 0) {
        setTableData(filteredData);
      } else {
        setTableData([]);
      }

      // Manage Last Key and Total Record (following receiver/index.tsx pattern)
      const newLastKey = response.data.receivers?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        const newPage = payload?.page || 0;
        const newPageSize = payload?.limit || pageSize;

        if (filteredData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys((prev) => ({ ...prev, [newPage]: newLastKey }));
      } else {
        // No more pages
        const newPage = payload?.page || 0;
        const newPageSize = payload?.limit || pageSize;
        setRowCount(newPage * newPageSize + filteredData.length);
        setCurrentLastKey("");
      }
    } catch (error) {
      console.error("Error fetching receiver accounts", error);
      setTableData([]);
      // Clear all
      setPage(0);
      setRowCount(0);
      setLastKeys({});
      setCurrentLastKey("");
    } finally {
      setIsLoading(false);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setIsLoading(true);

    let newFilters: PayloadType = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      receiver_id: receiverId,
    };

    // If click prev pagination
    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    // if select new row per page, reset page to 0, reset lastkey
    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        receiver_id: receiverId,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys({});
    } else {
      setPage(newValue.page);
    }

    fetchReceiverAccounts(newFilters);
  };

  const handleSelect = (account: ReceiverAccountData) => {
    onSelect(account);
    onClose();
  };

  const formatMethodDetails = (methodDetails: Record<string, any>) => {
    return Object.entries(methodDetails)
      .filter(([, value]) => value !== "")
      .map(([key, value]) => `${formatLabel(key)}: ${value}`)
      .join(" / ");
  };

  const formatLabel = (label: string) => {
    let modifiedLabel = label.split("_").join(" ");
    switch (label) {
      case "bsb":
        modifiedLabel = "BSB";
        break;
      case "bic":
        modifiedLabel = "BIC";
        break;
      case "iban":
        modifiedLabel = "IBAN";
        break;
    }
    return modifiedLabel;
  };

  const statusClasses = "status";

  const statusList = [
    {
      status: "unconfirmed",
      display: (
        <Badge className={`${statusClasses} status-unconfirmed`}>
          Unconfirmed
        </Badge>
      ),
    },
    {
      status: "sent_mail",
      display: (
        <Badge className={`${statusClasses} status-sent_mail`}>
          Email Sent
        </Badge>
      ),
    },
    {
      status: "confirmed",
      display: (
        <Badge className={`${statusClasses} status-confirmed`}>Confirmed</Badge>
      ),
    },
  ];

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  const MethodDetailsRenderer = (params: ICellRendererParams) => (
    <>{formatMethodDetails(params.data.method_details || {})}</>
  );

  const StatusRenderer = (params: ICellRendererParams) => {
    const status = params.data.confirm_status;
    const statusItem = statusList.find((item) => item.status === status);
    return <>{statusItem ? statusItem.display : null}</>;
  };

  const ActionsRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Select Account" arrow placement="top">
      <IconButton
        id={"button-select-" + params.data?.id}
        onClick={() => handleSelect(params.data)}
      >
        <MdOutlineAddCard className="h-5 w-5" />
      </IconButton>
    </Tooltip>
  );

  const columns: ColDef[] = [
    {
      field: "method_name",
      headerName: "Method",
      flex: 1,
      minWidth: 120,
      sortable: false,
    },
    {
      field: "method_details",
      headerName: "Account Details",
      flex: 2,
      minWidth: 300,
      sortable: false,
      cellRenderer: MethodDetailsRenderer,
    },
    {
      field: "confirm_status",
      headerName: "Status",
      width: 140,
      sortable: false,
      cellRenderer: StatusRenderer,
      cellClass: "ag-cell-status",
    },
    {
      field: "actions",
      headerName: "",
      width: 120,
      sortable: false,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const hidePagination = currentLastKey === "" && page === 0;

  return (
    <>
      {isOpen && (
        // <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} size="4xl">
        //   <ModalOverlay className="bg-[#000] !opacity-30" />
        //   <ModalContent className="dark:!bg-neutral-900 !z-[9999] !m-auto !w-full !max-w-[90%] lg:!max-w-[900px] !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[8vh]">
        //     <ModalBody className="p-6 md:p-7">
        //       <div className="mb-4 flex items-start gap-3">
        //         <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
        //           <svg
        //             viewBox="0 0 24 24"
        //             className="h-5 w-5"
        //             fill="currentColor"
        //           >
        //             <path d="M4 10v7h3v-7H4zm6 0v7h3v-7h-3zM2 22h19v-3H2v3zm14-12v7h3v-7h-3zm-4.5-9L2 6v2h19V6l-9.5-5z" />
        //           </svg>
        //         </div>
        //         <div>
        //           <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
        //             Select Payment Account
        //           </h1>
        //           <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
        //             Choose a receiver account for your payout
        //           </p>
        //         </div>
        //       </div>

        //       {/* DataGrid */}
        //       <Box className="w-full">
        //         <DataGrid
        //           sx={{
        //             "& .MuiDataGrid-cell": {
        //               padding: "8px 16px",
        //             },
        //             "& .MuiDataGrid-columnHeader": {
        //               padding: "8px 16px",
        //             },
        //             "& .MuiTablePagination-input": {
        //               marginRight: "-20px",
        //             },
        //             "& .MuiTablePagination-displayedRows": {
        //               display: "none",
        //             },
        //           }}
        //           loading={isLoading}
        //           rows={tableData || []}
        //           columns={columns}
        //           getRowId={(row) => row.id}
        //           rowCount={rowCount}
        //           sortingMode="server"
        //           paginationMode="server"
        //           rowHeight={48}
        //           columnHeaderHeight={48}
        //           paginationModel={{ page: page, pageSize: pageSize }}
        //           onPaginationModelChange={setPaginationModel}
        //           disableRowSelectionOnClick
        //           disableColumnFilter
        //           disableColumnMenu
        //           classes={customDataGridStyles}
        //           hideFooter={currentLastKey === "" && page === 0}
        //           autoHeight
        //           localeText={{
        //             noRowsLabel: "No receiver accounts found",
        //           }}
        //         />
        //       </Box>

        //       {/* Footer Buttons */}
        //       <div className="mt-6 flex justify-center">
        //         <Button
        //           onClick={onClose}
        //           className="btn btn-default"
        //         >
        //           Close
        //         </Button>
        //       </div>
        //     </ModalBody>
        //   </ModalContent>
        // </Modal>

        <Modal
          isOpen={isOpen}
          onClose={onClose}
          closeOnOverlayClick={false}
          size="4xl"
        >
          <ModalOverlay className="bg-[#000] !opacity-30" />

          <ModalContent className="dark:!bg-neutral-900 !z-[9999] !m-auto !w-full !max-w-[90%] !rounded-2xl !border !border-gray-200 !bg-white !shadow-2xl dark:!border-white/10 md:!top-[8vh] lg:!max-w-[900px]">
            <ModalBody className="p-0">
              <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-white/10 md:px-7">
                <div className="flex items-start gap-3">
                  <div className="mt-1 rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-300 dark:ring-primary-900/30">
                    <svg
                      viewBox="0 0 24 24"
                      className="h-6 w-6"
                      fill="currentColor"
                    >
                      <path d="M4 10v7h3v-7H4zm6 0v7h3v-7h-3zM2 22h19v-3H2v3zm14-12v7h3v-7h-3zm-4.5-9L2 6v2h19V6l-9.5-5z" />
                    </svg>
                  </div>

                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Select Payment Account
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Choose a receiver account for your payout
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-3xl border border-gray-200 text-gray-600 transition hover:bg-gray-50 hover:text-gray-700 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
                  aria-label="Close"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18 6 6 18" />
                    <path d="M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="max-h-[70vh] overflow-y-auto">
              <div className="mb-4 px-6 py-6 md:px-7">
                <Box className="dark:bg-neutral-900 w-full rounded-xl border border-gray-200 bg-white dark:border-white/10">
                  <div style={{ width: "100%" }}>
                    <AgGridReact
                      theme={themeAlpine}
                      rowData={isLoading ? [] : tableData}
                      columnDefs={columns}
                      defaultColDef={defaultColDef}
                      getRowId={(params) => params.data.id}
                      rowHeight={48}
                      headerHeight={44}
                      domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
                      suppressPaginationPanel={true}
                      loading={isLoading}
                      loadingOverlayComponent={CustomLoadingOverlay}
                      overlayNoRowsTemplate="No receiver accounts found"
                    />
                  </div>

                  <AgGridPagination
                    page={page}
                    pageSize={pageSize}
                    rowCount={rowCount}
                    onPaginationChange={setPaginationModel}
                    hidden={hidePagination}
                  />
                </Box>
              </div>
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default ReceiverAccountSelectorModal;
